import { getActiveUser } from "@noom/hive-auth";
import type {
  EvaluateProductEligibilityRequestProto_Body,
  EvaluateProductEligibilityResponseProto,
} from "@noom/noom-contracts/noom_contracts/billing/product_catalog/product_eligibility_service";
import type { EvaluationTriggerContextProto } from "@noom/noom-contracts/noom_contracts/billing/shared";

import { appConfig } from "src/config";
import { getCookieAccessCode } from "src/utils/authCookieParser";
import { FailedResponse, send } from "src/utils/fetch";
import { isUnloading } from "src/utils/lifecycle";
import { trackTask } from "src/utils/monitoring/events/tasks";
import getStore from "src/utils/redux/store";

function resolveAccessCode(): string | undefined {
  // Cookie before Redux so accessCode matches send()'s bearer (hive → cookie).
  return (
    getActiveUser()?.accessCode ??
    getCookieAccessCode() ??
    getStore().getState().userData.access_code
  );
}

// An unresolved verdict fail-closes the vendor gate, so absorb transient
// blips instead of costing the user branded on one bad round trip. send()
// has no timeout of its own, so the per-attempt timeout is what lets a retry
// trigger on a hung request. The final attempt runs un-capped: a slow but
// healthy endpoint should still land its response in Redux (even after the
// 5s cold-load cap in ENSURE_MED_PRODUCT_ELIGIBILITY has let the flow move
// on) rather than be discarded by a third timeout.
const MAX_ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 1500;

class AttemptTimeoutError extends Error {
  constructor() {
    super("evaluateProductEligibility attempt timed out");
  }
}

function withAttemptTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new AttemptTimeoutError());
    }, ATTEMPT_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function isRetryableError(error: unknown) {
  if (isUnloading()) {
    return false;
  }
  if (error instanceof FailedResponse) {
    // Only server errors: a 4xx (bad access code, auth) fails identically on
    // every attempt and just burns the budget.
    return (error.response?.status ?? 0) >= 500;
  }
  // Transport errors and per-attempt timeouts.
  return true;
}

export type EvaluateProductEligibilityOptions = {
  triggerContext?: EvaluationTriggerContextProto;
  retry?: boolean;
};

/**
 * `retry` is opt-in so only callers that asked for it (the vendor gate, where
 * an unresolved verdict fail-closes) get the multi-attempt behavior; other
 * consumers (Sermorelin cross-sell) keep the single un-capped attempt.
 */
export async function evaluateProductEligibility(
  productNames: string[],
  { triggerContext, retry = false }: EvaluateProductEligibilityOptions = {}
): Promise<EvaluateProductEligibilityResponseProto> {
  const accessCode = resolveAccessCode();
  if (!accessCode) {
    throw new Error("Access code is required to evaluate product eligibility");
  }
  if (productNames.length === 0) {
    throw new Error("At least one product name is required");
  }

  const body: EvaluateProductEligibilityRequestProto_Body = {
    accessCode,
    productNames,
    saleItemIds: [],
    limit: productNames.length,
    triggerContext,
  };

  const task = trackTask("EvaluateProductEligibility", {
    productCount: productNames.length,
  });
  task.start();

  const maxAttempts = retry ? MAX_ATTEMPTS : 1;

  // send() only ever throws Error subclasses (FailedResponse / NoomError).
  let lastError = new Error("evaluateProductEligibility failed");
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const request = send<EvaluateProductEligibilityResponseProto>(
        "POST",
        `${appConfig.BILLING_CORE_API_URL}/billing/v2/productEligibility/evaluate`,
        body
      );
      // eslint-disable-next-line no-await-in-loop
      const response = await (attempt < maxAttempts
        ? withAttemptTimeout(request)
        : request);
      task.success();
      return response;
    } catch (ex) {
      lastError = ex as Error;
      if (!isRetryableError(ex)) {
        break;
      }
    }
  }

  task.failed(lastError);
  throw lastError;
}
